import { useState } from 'react';

import { StatusMessage } from './StatusMessage.tsx';
import {
  downloadInvoicePdf,
  type DownloadInvoicePdfInput,
  type DownloadInvoicePdfResult,
} from '../pdf/download.ts';
import type { PdfInvoiceModel } from '../pdf/model.ts';
import type { PdfSignature } from '../pdf/signature.ts';

export interface DownloadPdfButtonProps {
  invoice: PdfInvoiceModel | null;
  signature?: PdfSignature | null;
  disabled?: boolean;
  className?: string;
  buttonClassName?: string;
  download?: (input: DownloadInvoicePdfInput) => Promise<DownloadInvoicePdfResult>;
}

const defaultButtonClassName =
  'inline-flex min-h-11 items-center justify-center rounded-full border border-line bg-paper px-4 py-2 text-sm font-medium text-ink transition hover:border-ochre/40 hover:bg-paper-deep disabled:cursor-not-allowed disabled:opacity-60';

export function DownloadPdfButton({
  invoice,
  signature = null,
  disabled = false,
  className,
  buttonClassName,
  download = downloadInvoicePdf,
}: DownloadPdfButtonProps): React.ReactElement {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isDisabled = disabled || pending || invoice === null;

  async function handleDownload(): Promise<void> {
    if (isDisabled || invoice === null) return;

    setError(null);
    setPending(true);
    try {
      const result = await download({ invoice, signature });
      if (!result.ok) setError(result.error);
    } catch {
      setError('Could not generate PDF. Try again or check the logs.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className={classNames('space-y-2', className)}>
      <button
        type="button"
        disabled={isDisabled}
        onClick={() => {
          void handleDownload();
        }}
        className={classNames(defaultButtonClassName, buttonClassName)}
      >
        {pending ? 'Preparing PDF...' : 'Download PDF'}
      </button>
      {error ? (
        <StatusMessage tone="error" className="max-w-sm">
          {error}
        </StatusMessage>
      ) : null}
    </div>
  );
}

function classNames(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(' ');
}
